import { useState, useCallback, useEffect } from 'react';
import { LabelTemplate, LabelElement, LABEL_WIDTH_MM, LABEL_HEIGHT_MM } from '@/types/label';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import type { Json } from '@/integrations/supabase/types';

interface DbTemplate {
  id: string;
  user_id: string;
  name: string;
  width: number;
  height: number;
  elements: Json;
  is_global: boolean;
  created_at: string;
  updated_at: string;
  category: string | null;
  field_config: Json | null;
}

const dbToTemplate = (db: DbTemplate): LabelTemplate => ({
  id: db.id,
  name: db.name,
  width: Number(db.width),
  height: Number(db.height),
  elements: db.elements as unknown as LabelElement[],
  createdAt: new Date(db.created_at).getTime(),
  updatedAt: new Date(db.updated_at).getTime(),
  isGlobal: db.is_global,
  category: db.category || undefined,
  fieldConfig: (db.field_config as any) || undefined,
});

export const useTemplateStorage = () => {
  const { user, isAdmin } = useAuth();
  const [savedTemplates, setSavedTemplates] = useState<LabelTemplate[]>([]);
  const [globalTemplates, setGlobalTemplates] = useState<LabelTemplate[]>([]);
  const [loading, setLoading] = useState(true);

  // Load templates from database on mount/user change
  useEffect(() => {
    const loadTemplates = async () => {
      if (!user) {
        setSavedTemplates([]);
        setGlobalTemplates([]);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const { data, error } = await supabase
          .from('templates')
          .select('*')
          .order('updated_at', { ascending: false });

        if (error) throw error;

        const templates = (data || []).map((row) => dbToTemplate(row as DbTemplate));
        
        // Separate user templates and global templates
        const userTemplates = templates.filter(t => !t.isGlobal && t.id);
        const globals = templates.filter(t => t.isGlobal);
        
        setSavedTemplates(userTemplates);
        setGlobalTemplates(globals);
      } catch (e) {
        console.error('Failed to load templates:', e);
        toast.error('Failed to load templates');
      } finally {
        setLoading(false);
      }
    };

    loadTemplates();
  }, [user]);

  const saveTemplate = useCallback(async (template: LabelTemplate, makeGlobal: boolean = false): Promise<LabelTemplate | null> => {
    if (!user) {
      toast.error('You must be logged in to save templates');
      return null;
    }

    // Only admins can save global templates
    if (makeGlobal && !isAdmin) {
      toast.error('Only administrators can create global templates');
      return null;
    }

    try {
      const isNew = !template.id || (!savedTemplates.find(t => t.id === template.id) && !globalTemplates.find(t => t.id === template.id));

      if (isNew) {
        // Insert new template
        const { data, error } = await supabase
          .from('templates')
          .insert({
            user_id: user.id,
            name: template.name,
            width: template.width || LABEL_WIDTH_MM,
            height: template.height || LABEL_HEIGHT_MM,
            elements: template.elements as unknown as Json,
            is_global: makeGlobal,
            category: template.category || null,
            field_config: (template.fieldConfig as unknown as Json) || null,
          })
          .select()
          .single();

        if (error) throw error;

        const newTemplate = dbToTemplate(data as DbTemplate);
        if (makeGlobal) {
          setGlobalTemplates(prev => [newTemplate, ...prev]);
        } else {
          setSavedTemplates(prev => [newTemplate, ...prev]);
        }
        toast.success(makeGlobal ? 'Global template saved' : 'Template saved');
        return newTemplate;
      } else {
        // Update existing template
        const { data, error } = await supabase
          .from('templates')
          .update({
            name: template.name,
            width: template.width || LABEL_WIDTH_MM,
            height: template.height || LABEL_HEIGHT_MM,
            elements: template.elements as unknown as Json,
            is_global: makeGlobal,
            category: template.category || null,
            field_config: (template.fieldConfig as unknown as Json) || null,
          })
          .eq('id', template.id)
          .select()
          .single();

        if (error) throw error;

        const updatedTemplate = dbToTemplate(data as DbTemplate);
        
        // Handle moving between global and user templates
        if (makeGlobal) {
          setSavedTemplates(prev => prev.filter(t => t.id !== updatedTemplate.id));
          setGlobalTemplates(prev => {
            const exists = prev.find(t => t.id === updatedTemplate.id);
            if (exists) {
              return prev.map(t => t.id === updatedTemplate.id ? updatedTemplate : t);
            }
            return [updatedTemplate, ...prev];
          });
        } else {
          setGlobalTemplates(prev => prev.filter(t => t.id !== updatedTemplate.id));
          setSavedTemplates(prev => {
            const exists = prev.find(t => t.id === updatedTemplate.id);
            if (exists) {
              return prev.map(t => t.id === updatedTemplate.id ? updatedTemplate : t);
            }
            return [updatedTemplate, ...prev];
          });
        }
        
        toast.success('Template updated');
        return updatedTemplate;
      }
    } catch (e) {
      console.error('Failed to save template:', e);
      toast.error('Failed to save template');
      return null;
    }
  }, [user, isAdmin, savedTemplates, globalTemplates]);

  const deleteTemplate = useCallback(async (templateId: string) => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('templates')
        .delete()
        .eq('id', templateId);

      if (error) throw error;

      setSavedTemplates(prev => prev.filter(t => t.id !== templateId));
      setGlobalTemplates(prev => prev.filter(t => t.id !== templateId));
      toast.success('Template deleted');
    } catch (e) {
      console.error('Failed to delete template:', e);
      toast.error('Failed to delete template');
    }
  }, [user]);

  const duplicateTemplate = useCallback(async (template: LabelTemplate, makeGlobal: boolean = false): Promise<LabelTemplate | null> => {
    if (!user) {
      toast.error('You must be logged in to duplicate templates');
      return null;
    }

    // Only admins can create global duplicates
    if (makeGlobal && !isAdmin) {
      toast.error('Only administrators can create global templates');
      return null;
    }

    try {
      const { data, error } = await supabase
        .from('templates')
        .insert({
          user_id: user.id,
          name: `${template.name} (Copy)`,
          width: template.width,
          height: template.height,
          elements: template.elements as unknown as Json,
          is_global: makeGlobal,
          category: template.category || null,
        })
        .select()
        .single();

      if (error) throw error;

      const duplicate = dbToTemplate(data as DbTemplate);
      if (makeGlobal) {
        setGlobalTemplates(prev => [duplicate, ...prev]);
      } else {
        setSavedTemplates(prev => [duplicate, ...prev]);
      }
      toast.success('Template duplicated');
      return duplicate;
    } catch (e) {
      console.error('Failed to duplicate template:', e);
      toast.error('Failed to duplicate template');
      return null;
    }
  }, [user, isAdmin]);

  const exportTemplate = useCallback((template: LabelTemplate) => {
    const json = JSON.stringify(template, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${template.name.replace(/\s+/g, '-').toLowerCase()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }, []);

  const importTemplate = useCallback(async (file: File, makeGlobal: boolean = false): Promise<LabelTemplate | null> => {
    if (!user) {
      toast.error('You must be logged in to import templates');
      return null;
    }

    // Only admins can import global templates
    if (makeGlobal && !isAdmin) {
      toast.error('Only administrators can import global templates');
      return null;
    }

    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const template = JSON.parse(e.target?.result as string);
          if (!template.elements || !Array.isArray(template.elements)) {
            throw new Error('Invalid template format');
          }

          const { data, error } = await supabase
            .from('templates')
            .insert({
              user_id: user.id,
              name: template.name || 'Imported Template',
              width: LABEL_WIDTH_MM,
              height: LABEL_HEIGHT_MM,
              elements: template.elements as unknown as Json,
              is_global: makeGlobal,
              category: template.category || null,
            })
            .select()
            .single();

          if (error) throw error;

          const imported = dbToTemplate(data as DbTemplate);
          if (makeGlobal) {
            setGlobalTemplates(prev => [imported, ...prev]);
          } else {
            setSavedTemplates(prev => [imported, ...prev]);
          }
          toast.success(makeGlobal ? 'Global template imported' : 'Template imported');
          resolve(imported);
        } catch (e) {
          console.error('Failed to import template:', e);
          toast.error('Failed to parse template file');
          resolve(null);
        }
      };
      reader.onerror = () => {
        toast.error('Failed to read file');
        resolve(null);
      };
      reader.readAsText(file);
    });
  }, [user, isAdmin]);

  const toggleGlobalStatus = useCallback(async (template: LabelTemplate): Promise<LabelTemplate | null> => {
    if (!isAdmin) {
      toast.error('Only administrators can change global status');
      return null;
    }

    const newIsGlobal = !template.isGlobal;
    
    try {
      const { data, error } = await supabase
        .from('templates')
        .update({ is_global: newIsGlobal })
        .eq('id', template.id)
        .select()
        .single();

      if (error) throw error;

      const updated = dbToTemplate(data as DbTemplate);
      
      if (newIsGlobal) {
        setSavedTemplates(prev => prev.filter(t => t.id !== template.id));
        setGlobalTemplates(prev => [updated, ...prev]);
        toast.success('Template is now global');
      } else {
        setGlobalTemplates(prev => prev.filter(t => t.id !== template.id));
        setSavedTemplates(prev => [updated, ...prev]);
        toast.success('Template is now private');
      }
      
      return updated;
    } catch (e) {
      console.error('Failed to toggle global status:', e);
      toast.error('Failed to update template');
      return null;
    }
  }, [isAdmin]);

  const updateCategory = useCallback(async (templateId: string, category: string | undefined) => {
    try {
      const { error } = await supabase
        .from('templates')
        .update({ category: category || null })
        .eq('id', templateId);

      if (error) throw error;

      setSavedTemplates(prev => prev.map(t => t.id === templateId ? { ...t, category } : t));
      setGlobalTemplates(prev => prev.map(t => t.id === templateId ? { ...t, category } : t));
      toast.success('Category updated');
    } catch (e) {
      console.error('Failed to update category:', e);
      toast.error('Failed to update category');
    }
  }, []);

  return {
    savedTemplates,
    globalTemplates,
    loading,
    saveTemplate,
    deleteTemplate,
    duplicateTemplate,
    exportTemplate,
    importTemplate,
    toggleGlobalStatus,
    updateCategory,
  };
};
